import { useEffect, useMemo, useState } from 'react'
import { TASK_STATUS } from '@ovrsee/shared'
import { useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { Topbar } from '../../components/Topbar.js'
import { DataTable, type Column } from '../../components/DataTable.js'
import { EmptyState } from '../../components/EmptyState.js'
import { ErrorState } from '../../components/ErrorState.js'
import { LoadingState } from '../../components/LoadingState.js'
import { PriorityDot } from '../../components/PriorityDot.js'
import { StatusChip } from '../../components/StatusChip.js'
import { useAuth } from '../../lib/auth.js'
import { usePageTitle } from '../../lib/page-title.js'
import { ReviewTaskModal } from './ReviewTaskModal.js'
import { AssignTaskModal } from './AssignTaskModal.js'
import { TemplatesModal } from './TemplatesModal.js'
import {
  taskQueryFromSearchParams,
  useAssignableUsers,
  useDebounced,
  useTasks,
  type TaskListItem,
} from './useTasks.js'
import { Button, Input } from '../../ui/index.js'

/**
 * The pills from manager-tasks.jsx. "In progress" deliberately covers ASSIGNED as well
 * as IN_PROGRESS - from a manager's point of view unstarted work is still in flight - and
 * no pill sends OVERDUE, which is derived rather than stored and has its own parameter.
 */
export const FILTERS: readonly { id: string; label: string; params: Record<string, string> }[] = [
  { id: 'all', label: 'All', params: {} },
  { id: 'awaiting', label: 'Awaiting review', params: { status: TASK_STATUS.SUBMITTED } },
  { id: 'progress', label: 'In progress', params: { status: TASK_STATUS.IN_PROGRESS } },
  { id: 'returned', label: 'Returned', params: { status: TASK_STATUS.RETURNED } },
  { id: 'blocked', label: 'Blocked', params: { status: TASK_STATUS.BLOCKED } },
  { id: 'approved', label: 'Approved', params: { status: TASK_STATUS.APPROVED } },
]

export function ManagerTasks() {
  usePageTitle('Tasks')
  const { api } = useAuth()
  const [assignOpen, setAssignOpen] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [templatesOpen, setTemplatesOpen] = useState(false)
  const [params, setParams] = useSearchParams()
  const [reviewTaskId, setReviewTaskId] = useState<string | null>(() => params.get('task'))
  const query = useMemo(() => taskQueryFromSearchParams(params), [params])
  const [search, setSearch] = useState(query.q ?? '')
  const debouncedSearch = useDebounced(search)
  const assigneesQuery = useAssignableUsers()
  const departmentsQuery = useQuery({
    queryKey: ['task-filter-departments'],
    queryFn: () => api.get<{ readonly id: string; readonly name: string }[]>('/api/departments'),
  })

  // A bell deep link can arrive while this screen is already mounted.
  useEffect(() => {
    const linked = params.get('task')
    if (linked !== null) setReviewTaskId(linked)
  }, [params])

  useEffect(() => {
    if (query.q !== search) setSearch(query.q ?? '')
  }, [query.q])

  useEffect(() => {
    const next = new URLSearchParams(params)
    const current = next.get('q') ?? ''
    const desired = debouncedSearch.trim()
    if (current === desired) return
    if (desired === '') next.delete('q')
    else next.set('q', desired)
    setParams(next, { replace: true })
  }, [debouncedSearch, params, setParams])

  const writeParams = (updates: Record<string, string | null | undefined>) => {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(updates)) {
      if (value === undefined || value === null || value === '') next.delete(key)
      else next.set(key, value)
    }
    setParams(next, { replace: true })
  }

  const closeReview = () => {
    setReviewTaskId(null)
    // Delete ONLY `task`. The original line here was
    //   setParams({}, { replace: true })
    // which wipes the whole query string - harmless when `task` was the only
    // parameter, but it now clears every filter the panel owns the moment a
    // manager closes a modal opened from a notification.
    if (params.get('task') === null) return
    const next = new URLSearchParams(params)
    next.delete('task')
    setParams(next, { replace: true })
  }

  const clearPanel = () => {
    writeParams({
      assigneeId: null,
      departmentId: null,
      priority: null,
      complexityMin: null,
      complexityMax: null,
      from: null,
      to: null,
    })
  }

  const complexityBand: 'low' | 'medium' | 'high' | 'all' =
    query.complexityMin === 1 && query.complexityMax === 3
      ? 'low'
      : query.complexityMin === 4 && query.complexityMax === 7
        ? 'medium'
        : query.complexityMin === 8 && query.complexityMax === 10
          ? 'high'
          : 'all'

  const selectedFilterId =
    query.status === undefined
      ? 'all'
      : FILTERS.find((candidate) => candidate.params.status === query.status)?.id ?? 'all'

  const panelCount =
    (query.assigneeId === undefined ? 0 : 1) +
    (query.departmentId === undefined ? 0 : 1) +
    (query.priority === undefined ? 0 : 1) +
    (query.complexityMin === undefined && query.complexityMax === undefined ? 0 : 1) +
    (query.from === undefined && query.to === undefined ? 0 : 1)

  const tasks = useTasks(query)

  const columns: readonly Column<TaskListItem>[] = [
    {
      key: 'reference',
      header: 'ID',
      width: 92,
      render: (row) => (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-muted)' }}>
          {row.reference}
        </span>
      ),
    },
    {
      key: 'title',
      header: 'Task',
      render: (row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <PriorityDot priority={row.priority} />
          <span style={{ color: 'var(--fg)', fontWeight: 500 }}>{row.title}</span>
          <span style={{ color: 'var(--fg-subtle)', fontSize: 12 }}>{row.category}</span>
        </span>
      ),
    },
    { key: 'assignee', header: 'Assignee', render: (row) => row.assignee?.name ?? '—' },
    { key: 'priority', header: 'Priority', render: (row) => titleCase(row.priority) },
    { key: 'complexity', header: 'Complexity', numeric: true, render: (row) => row.complexity },
    {
      key: 'due',
      header: 'Due',
      numeric: true,
      render: (row) => (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            color: row.isOverdue ? 'var(--negative)' : 'var(--fg-2)',
          }}
        >
          {row.dueIn}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusChip status={row.status} isOverdue={row.isOverdue} />,
    },
  ]

  return (
    <>
      <Topbar
        title="Tasks"
        eyebrow="All tasks"
        actions={
          <>
            <Button kind="tertiary" size="small" onClick={() => setTemplatesOpen(true)}>
              Templates
            </Button>
            <Button kind="primary" size="small" aria-label="Assign task" onClick={() => setAssignOpen(true)}>
              + Task
            </Button>
          </>
        }
      />

      <AssignTaskModal isOpen={assignOpen} onClose={() => setAssignOpen(false)} />
      <TemplatesModal isOpen={templatesOpen} onClose={() => setTemplatesOpen(false)} />
      <ReviewTaskModal taskId={reviewTaskId} onClose={closeReview} />

      <div className="page">
        <div className="page-section">
          <div className="row-between" style={{ marginBottom: 'var(--o-space-5)', flexWrap: 'wrap', gap: 12 }}>
            {/* The status pills stay in the header because they are the highest-frequency filter. */}
            <div
              className="row-actions"
              role="group"
              aria-label="Filter tasks"
              style={{ flexWrap: 'wrap' }}
            >
              {FILTERS.map((option) => {
                const isActive = option.id === selectedFilterId
                return (
                  <button
                    key={option.id}
                    type="button"
                    aria-label={option.label}
                    aria-pressed={isActive}
                    onClick={() => {
                      if (option.params.status === undefined) {
                        writeParams({ status: null })
                        return
                      }
                      writeParams({ status: option.params.status })
                    }}
                    style={{
                      padding: '5px 12px',
                      borderRadius: 999,
                      fontSize: 12.5,
                      fontWeight: 500,
                      border: `1px solid ${isActive ? 'var(--mauve-200)' : 'transparent'}`,
                      background: isActive ? 'var(--brand-soft)' : 'transparent',
                      color: isActive ? 'var(--mauve-700)' : 'var(--fg-muted)',
                    }}
                  >
                    {option.label === 'In progress' ? 'In progress.' : option.label}
                  </button>
                )
              })}
            </div>

            <div style={{ width: 240 }}>
              <Input
                placeholder="Search tasks..."
                value={search}
                onChange={setSearch}
                aria-label="Search tasks"
              />
            </div>

            <Button kind="secondary" size="small" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((current) => !current)}>
              {`Filters (${panelCount})`}
            </Button>
          </div>

          {filtersOpen ? (
            <section
              aria-label="Task filters"
              style={{
                display: 'grid',
                gap: 'var(--o-space-3)',
                marginBottom: 'var(--o-space-5)',
                padding: 'var(--o-space-4)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)',
                background: 'var(--surface)',
              }}
            >
              <div style={{ display: 'grid', gap: 'var(--o-space-2)' }}>
                <div className="card-label">Assignee</div>
                <div role="group" aria-label="Assignee filter" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <Button
                    type="button"
                    size="small"
                    kind={query.assigneeId === undefined ? 'primary' : 'secondary'}
                    aria-pressed={query.assigneeId === undefined}
                    onClick={() => writeParams({ assigneeId: null })}
                  >
                    All
                  </Button>
                  {(assigneesQuery.data ?? []).map((person) => (
                    <Button
                      key={person.id}
                      type="button"
                      size="small"
                      kind={query.assigneeId === person.id ? 'primary' : 'secondary'}
                      aria-pressed={query.assigneeId === person.id}
                      onClick={() => writeParams({ assigneeId: person.id })}
                    >
                      {person.name}
                    </Button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gap: 'var(--o-space-2)' }}>
                <div className="card-label">Department</div>
                <div role="group" aria-label="Department filter" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <Button
                    type="button"
                    size="small"
                    kind={query.departmentId === undefined ? 'primary' : 'secondary'}
                    aria-pressed={query.departmentId === undefined}
                    onClick={() => writeParams({ departmentId: null })}
                  >
                    All
                  </Button>
                  {(departmentsQuery.data ?? []).map((department) => (
                    <Button
                      key={department.id}
                      type="button"
                      size="small"
                      kind={query.departmentId === department.id ? 'primary' : 'secondary'}
                      aria-pressed={query.departmentId === department.id}
                      onClick={() => writeParams({ departmentId: department.id })}
                    >
                      {department.name}
                    </Button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gap: 'var(--o-space-2)' }}>
                <div className="card-label">Priority</div>
                <div role="group" aria-label="Priority filter" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <Button
                    type="button"
                    size="small"
                    kind={query.priority === undefined ? 'primary' : 'secondary'}
                    aria-pressed={query.priority === undefined}
                    onClick={() => writeParams({ priority: null })}
                  >
                    All
                  </Button>
                  {(['LOW', 'MEDIUM', 'HIGH'] as const).map((priority) => (
                    <Button
                      key={priority}
                      type="button"
                      size="small"
                      kind={query.priority === priority ? 'primary' : 'secondary'}
                      aria-pressed={query.priority === priority}
                      onClick={() => writeParams({ priority })}
                    >
                      {priority}
                    </Button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gap: 'var(--o-space-2)' }}>
                <div className="card-label">Complexity</div>
                <div role="group" aria-label="Complexity filter" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <Button
                    type="button"
                    size="small"
                    kind={complexityBand === 'all' ? 'primary' : 'secondary'}
                    aria-pressed={complexityBand === 'all'}
                    onClick={() => writeParams({ complexityMin: null, complexityMax: null })}
                  >
                    All
                  </Button>
                  <Button
                    type="button"
                    size="small"
                    kind={complexityBand === 'low' ? 'primary' : 'secondary'}
                    aria-pressed={complexityBand === 'low'}
                    onClick={() => writeParams({ complexityMin: '1', complexityMax: '3' })}
                  >
                    Low
                  </Button>
                  <Button
                    type="button"
                    size="small"
                    kind={complexityBand === 'medium' ? 'primary' : 'secondary'}
                    aria-pressed={complexityBand === 'medium'}
                    onClick={() => writeParams({ complexityMin: '4', complexityMax: '7' })}
                  >
                    Medium
                  </Button>
                  <Button
                    type="button"
                    size="small"
                    kind={complexityBand === 'high' ? 'primary' : 'secondary'}
                    aria-pressed={complexityBand === 'high'}
                    onClick={() => writeParams({ complexityMin: '8', complexityMax: '10' })}
                  >
                    High
                  </Button>
                </div>
              </div>

              <div style={{ display: 'grid', gap: 'var(--o-space-2)' }}>
                <div className="card-label">Deadline</div>
                <div role="group" aria-label="Deadline filter" style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                  <Input
                    type="datetime-local"
                    aria-label="Deadline from"
                    value={query.from === undefined ? '' : query.from.slice(0, 16)}
                    onChange={(value) => writeParams({ from: value === '' ? null : new Date(value).toISOString() })}
                  />
                  <Input
                    type="datetime-local"
                    aria-label="Deadline to"
                    value={query.to === undefined ? '' : query.to.slice(0, 16)}
                    onChange={(value) => writeParams({ to: value === '' ? null : new Date(value).toISOString() })}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <Button kind="secondary" size="small" onClick={clearPanel}>
                  Clear
                </Button>
                <div style={{ color: 'var(--fg-muted)', fontSize: 12, alignSelf: 'center' }}>Active filters: {panelCount}</div>
              </div>
            </section>
          ) : null}

          {tasks.isPending ? <LoadingState label="Loading tasks" /> : null}

          {tasks.isError ? <ErrorState error={tasks.error} onRetry={() => void tasks.refetch()} /> : null}

          {tasks.isSuccess ? (
            <>
              <DataTable
                columns={columns}
                rows={tasks.data.items}
                getRowKey={(row) => row.id}
                onRowClick={(row) => setReviewTaskId(row.id)}
                empty={
                  debouncedSearch.trim() === '' ? (
                    <EmptyState
                      title="No tasks yet"
                      hint="Assign the first one and it will appear here."
                    />
                  ) : (
                    <EmptyState
                      title="No tasks match that search"
                      hint="Try a different word, or clear the search to see everything."
                    />
                  )
                }
              />
              <div
                style={{
                  marginTop: 'var(--o-space-4)',
                  fontSize: 12,
                  color: 'var(--fg-muted)',
                }}
              >
                Showing {tasks.data.items.length} of {tasks.data.total}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  )
}

const titleCase = (value: string) => value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ')
